import { useEffect, useRef } from 'react';
import { ExposureToggle } from '../ExposureToggle';
import { HeatmapLoadingState } from '../HeatmapLoadingState';
import { TickerSelector } from '../TickerSelector';
import { TrinityPanelTable, type TrinityPanelTableHandle } from './TrinityPanelTable';
import {
  getHeatmapKey,
  useHeatmapStore,
  type ExposureType,
  type Ticker,
} from '@/store/heatmapStore';
import type { SupportedSymbol } from '@/lib/symbols';
import type { AutoCenterMode } from '@/lib/autoCenterPreference';
import type { TrinityPanelState } from '@/lib/trinityPanels';

interface TrinityPanelProps {
  panel: TrinityPanelState;
  symbols: readonly SupportedSymbol[];
  autoCenterMode: AutoCenterMode;
  visibleStrikeCount?: number | null;
  onTickerChange: (ticker: Ticker) => void;
  onTypeChange: (type: ExposureType) => void;
}

export function TrinityPanel({
  panel,
  symbols,
  autoCenterMode,
  visibleStrikeCount,
  onTickerChange,
  onTypeChange,
}: TrinityPanelProps) {
  const heatmapKey = getHeatmapKey(panel.ticker, panel.type);
  const envelope = useHeatmapStore((state) => state.heatmaps[heatmapKey] ?? null);
  const connectionStatus = useHeatmapStore((state) => state.connectionStatus);
  const bootstrapStatus = useHeatmapStore((state) => state.bootstrapStatus);
  const tableRef = useRef<TrinityPanelTableHandle>(null);
  const lastCenteredTargetRef = useRef<string | null>(null);

  const heatmapData = envelope?.data ?? null;
  const spotPrice = heatmapData?.spotPrice ?? null;
  const isLoading =
    envelope == null &&
    connectionStatus !== 'retrying' &&
    bootstrapStatus !== 'failed' &&
    bootstrapStatus !== 'complete';

  useEffect(() => {
    if (autoCenterMode === 'off' || heatmapData == null) {
      return;
    }

    const targetKey = `${heatmapKey}:${autoCenterMode}`;
    if (lastCenteredTargetRef.current === targetKey) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      if (autoCenterMode === 'spot') {
        tableRef.current?.scrollToSpot();
      } else {
        tableRef.current?.scrollToKing();
      }
      lastCenteredTargetRef.current = targetKey;
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [autoCenterMode, heatmapData, heatmapKey]);

  const emptyStateMessage =
    envelope != null
      ? null
      : connectionStatus === 'retrying'
        ? `Reconnecting to live ${panel.type.toUpperCase()} stream for ${panel.ticker}...`
        : bootstrapStatus === 'failed'
          ? 'Unable to load cached heatmaps. Waiting for live updates...'
          : bootstrapStatus === 'complete'
            ? `No cached ${panel.type.toUpperCase()} heatmap for ${panel.ticker}. Waiting for live update...`
            : `Loading ${panel.type.toUpperCase()} for ${panel.ticker}...`;

  return (
    <section
      aria-label={`${panel.ticker} ${panel.type.toUpperCase()} panel`}
      className="flex h-[72dvh] min-h-[320px] min-w-0 flex-none flex-col overflow-hidden rounded-[10px] border border-white/[.08] bg-[#0d0d0d] min-[900px]:h-auto min-[900px]:flex-1"
    >
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-white/[.06] px-2.5 py-2">
        <div className="min-w-[120px] max-w-[180px] flex-1">
          <TickerSelector
            symbols={symbols}
            activeTicker={panel.ticker}
            spotPrice={null}
            onTickerChange={onTickerChange}
          />
        </div>
        <ExposureToggle type={panel.type} onTypeChange={onTypeChange} />
        {spotPrice != null && (
          <span className="ml-auto flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-green-400 shadow-[0_0_4px_rgba(74,222,128,0.3)]" />
            <span className="font-mono text-[11px] font-medium text-zinc-200">
              ${spotPrice.toFixed(2)}
            </span>
          </span>
        )}
      </div>

      {heatmapData != null ? (
        <TrinityPanelTable
          ref={tableRef}
          rows={heatmapData.rows}
          spotPrice={heatmapData.spotPrice}
          type={panel.type}
          visibleStrikeCount={visibleStrikeCount}
        />
      ) : isLoading ? (
        <HeatmapLoadingState
          message={`Loading ${panel.type.toUpperCase()} for ${panel.ticker}...`}
        />
      ) : (
        <div className="flex flex-1 items-center justify-center px-4 text-center">
          <span className="font-mono text-[11px] text-neutral-500">{emptyStateMessage}</span>
        </div>
      )}
    </section>
  );
}
